import { useState, useEffect } from 'react';
import {
  Table,
  Form,
  Select,
  Input,
  Button,
  Card,
  Col,
  message,
  DatePicker,
  Tag,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import EllipsisCell from '@/components/common/EllipsisCell';
import * as reportService from '../../../services/report/reportService';
import { ORDER_STATUS_OPTIONS, CHANNEL_OPTIONS, getOrderStatusChartColor, PAYMENT_TYPE_TAG_COLORS, PAYMENT_TYPE_FILTER_OPTIONS } from '../../../constants/order';

const { RangePicker } = DatePicker;

// 对账数据状态口径（2026-10-16 客户要求恢复全量状态筛选）：下拉提供全部 6 个订单状态（ORDER_STATUS_OPTIONS），
// 后端不设强制状态范围（status 可空查全部）；默认选中 Outbound Completed（initialValues，2026-10-15 客户要求）

// 服务端排序白名单（2026-10-15 支付明细表改造扩展）：Order Create Time 命中 idx_warehouse_order_create、
// Order ID 命中 idx_warehouse_order_no；默认排序 order_create_time DESC, order_no DESC, id DESC（同单多支付行分页稳定）
const SORTABLE_COLUMNS = ['orderCreateTime', 'orderNo'];

// 金额展示：฿ + 千分位 + 固定三位小数（与订单列表/payment-invoice 一致）；空值单独处理显示 '-'
const formatTHB = (v) => (v == null ? '-' : `฿${Number(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`);

const CodReconciliationReport = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [riderOptions, setRiderOptions] = useState([]);
  const [posOptions, setPosOptions] = useState([]);
  // Total 汇总行：筛选条件全量行支付金额合计（后端 SUM(payment_amount)，非当前页合计）；初始未查询为 null 显示 '-'
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`,
  });
  // 服务端排序受控状态（2026-10-15 支付明细表改造：排序白名单扩为 Order Create Time + Order ID 两列）：
  // 初始/重置态 = undefined（箭头全灭，代表服务端默认排序 Order Create Time 降序；首次请求仍显式携带默认排序参数）；
  // 从默认态出发的 AntD 三态：首次点击 ascend → descend → 第三次 cancel 回默认（与客户确认的 升→降→取消 一致）；
  // 排序跨翻页/筛选保留
  const [sortState, setSortState] = useState({ field: undefined, order: undefined });

  const renderPaymentTypeTag = (paymentType) => {
    if (!paymentType) return '-';
    const color = PAYMENT_TYPE_TAG_COLORS[paymentType];
    if (color) {
      return <Tag color={color}>{paymentType}</Tag>;
    }
    // 未命中映射：回退灰色原值 Tag，不翻译、不丢弃
    return <Tag>{paymentType}</Tag>;
  };

  // 支付明细行级展示（一单多支付行，订单维度字段逐行重复）；rowKey 用支付行 id（orderNo 不再唯一）
  const columns = [
    {
      title: t('report.codReconciliation.storeId'),
      dataIndex: 'storeId',
      key: 'storeId',
      width: 110,
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>
    },
    {
      title: t('report.codReconciliation.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: 150,
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>
    },
    {
      title: t('report.codReconciliation.rider'),
      dataIndex: 'driverName',
      key: 'driverName',
      width: 220,
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>
    },
    {
      title: t('report.codReconciliation.orderCreateTime'),
      dataIndex: 'orderCreateTime',
      key: 'orderCreateTime',
      width: 190,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>
    },
    {
      title: t('report.codReconciliation.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200,
      // 蓝色仅视觉样式，不跳转详情（客户确认）
      render: (text) => <EllipsisCell><span style={{ color: '#1677ff' }}>{text}</span></EllipsisCell>
    },
    {
      title: t('report.codReconciliation.status'),
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status) => {
        if (!status) return '-';
        const option = ORDER_STATUS_OPTIONS.find((opt) => opt.value === status);
        const color = getOrderStatusChartColor(status);
        const displayText = option ? t(option.label) : status;
        return (
          <EllipsisCell>
            <span style={{ color, fontWeight: 500 }}>
              {displayText}
            </span>
          </EllipsisCell>
        );
      },
    },
    {
      title: t('report.codReconciliation.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel) => {
        if (!channel) return '-';
        const option = CHANNEL_OPTIONS.find((opt) => opt.value === channel);
        return <Tag color={channel === 'LOTUS' ? 'blue' : 'purple'}>{option ? t(option.label) : channel}</Tag>;
      },
    },
    {
      title: t('report.codReconciliation.posNo'),
      dataIndex: 'posNumber',
      key: 'posNumber',
      width: 140,
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>
    },
    {
      title: t('report.codReconciliation.paymentType'),
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 200,
      // 后端已派生组合码（PODCASH/PODQR/POLCC/...），沿用 PAYMENT_TYPE_TAG_COLORS Tag
      render: renderPaymentTypeTag,
    },
    {
      title: t('report.codReconciliation.paymentAmount'),
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      width: 200,
      align: 'right',
      render: (v) => <EllipsisCell>{formatTHB(v)}</EllipsisCell>
    },
  ].map((col) => ({
    ...col,
    // 2026-10-15 支付明细表改造：Order Create Time / Order ID 两列支持服务端排序（命中专用索引），
    // 其余列服务端白名单一律回落默认 order_create_time DESC, order_no DESC；
    // 受控 sortOrder 仅白名单列设置，箭头与请求参数永远同步
    ...(SORTABLE_COLUMNS.includes(col.dataIndex) ? {
      sorter: true,
      sortOrder: sortState.field === col.dataIndex ? sortState.order : undefined,
    } : {}),
  }));

  const buildSearchParams = (formValues) => {
    const searchParams = {};
    if (formValues.orderCreateDate && formValues.orderCreateDate.length === 2) {
      searchParams.orderCreateDateStart = formValues.orderCreateDate[0].startOf('day').valueOf();
      searchParams.orderCreateDateEnd = formValues.orderCreateDate[1].endOf('day').valueOf();
    }
    if (formValues.driverName) searchParams.driverName = formValues.driverName;
    if (formValues.orderNo) searchParams.orderNo = formValues.orderNo.trim();
    if (formValues.status) searchParams.status = formValues.status;
    if (formValues.channel) searchParams.channel = formValues.channel;
    if (formValues.posNumber) searchParams.posNumber = formValues.posNumber;
    if (formValues.paymentType) searchParams.paymentType = formValues.paymentType;
    // deliveredPaymentType 筛选已随支付明细表改造移除（delivered 系列字段废弃）
    return searchParams;
  };

  // AntD sorter 三态 → 服务端排序参数：ascend→asc、descend→desc；cancel（order 为空）不传，
  // 由服务端默认 order_create_time DESC 兜底（spec：第三次点击取消恢复默认排序）
  const buildSortParams = (currentSort) => {
    if (!currentSort || !currentSort.order) return {};
    return { sortKey: currentSort.field, sortOrder: currentSort.order === 'ascend' ? 'asc' : 'desc' };
  };

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...buildSearchParams(form.getFieldsValue()),
        ...params,
      };

      const response = await reportService.pageCodReconciliation(searchParams);
      if (response.success) {
        // 支付明细表改造后响应结构：{ items, total, currentPage, pageSize, totalPaymentAmount }
        const data = response.data || {};
        setDataSource(data.items || []);
        setTotalPaymentAmount(data.totalPaymentAmount != null ? data.totalPaymentAmount : null);
        setPagination((prev) => ({
          ...prev,
          current: data.currentPage || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Query COD reconciliation report failed:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRiderOptions = async () => {
    try {
      const response = await reportService.getCodRiderOptions();
      if (response.success) {
        setRiderOptions(response.data || []);
      }
    } catch (error) {
      console.error('Query COD rider options failed:', error);
    }
  };

  // POS 机号下拉选项（模式参照 rider-option）
  const fetchPosOptions = async () => {
    try {
      const response = await reportService.getCodPosOptions();
      if (response.success) {
        setPosOptions(response.data || []);
      }
    } catch (error) {
      console.error('Query COD POS options failed:', error);
    }
  };

  useEffect(() => {
    fetchData({ sortKey: 'orderCreateTime', sortOrder: 'desc' });
    fetchRiderOptions();
    fetchPosOptions();
  }, []);

  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 筛选不清除排序（spec：自定义排序下筛选保持原排序）
    fetchData({ currentPage: 1, ...buildSortParams(sortState) });
  };

  const handleReset = () => {
    form.resetFields();
    // 重置恢复默认排序（Order Create Time 降序；sortState 回 undefined = 箭头全灭的默认态，请求仍显式带默认排序参数）
    setSortState({ field: undefined, order: undefined });
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1, sortKey: 'orderCreateTime', sortOrder: 'desc' });
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const searchParams = { ...buildSearchParams(form.getFieldsValue()), ...buildSortParams(sortState) };

      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await reportService.exportCodReconciliation(searchParams);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('common.message.exportSuccess') || 'Export successful');
      } else {
        message.destroy();
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Export COD reconciliation report failed:', error);
      message.destroy();
      message.error(t('common.message.operationFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleTableChange = (paginationConfig, _filters, sorter) => {
    // 受控排序：sorter.order 为空（cancel 态）= 无自定义排序，箭头全灭、请求不带排序参数（服务端默认兜底）
    const nextSort = sorter && sorter.order ? { field: sorter.field, order: sorter.order } : { field: undefined, order: undefined };
    setSortState(nextSort);
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      ...buildSortParams(nextSort),
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
        initialValues={{
          orderCreateDate: [dayjs().startOf('day'), dayjs().endOf('day')],
          status: 'OUTBOUND_COMPLETED',
        }}
      >
        <Col span={4}>
          <Form.Item name="driverName" label={t('report.codReconciliation.rider')}>
            <Select placeholder={t('report.codReconciliation.allRiders')} options={riderOptions} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="orderNo" label={t('report.codReconciliation.orderNo')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6} className="filter-col-date-range">
          <Form.Item name="orderCreateDate" label={t('report.codReconciliation.orderCreateDate')}>
            <RangePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name="status" label={t('report.codReconciliation.status')}>
            <Select placeholder={t('common.pleaseSelect')} options={ORDER_STATUS_OPTIONS.map(option => ({ value: option.value, label: t(option.label) }))} allowClear />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name="channel" label={t('report.codReconciliation.channel')}>
            <Select placeholder={t('common.pleaseSelect')} options={CHANNEL_OPTIONS.map(option => ({ value: option.value, label: t(option.label) }))} allowClear />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Form.Item name="posNumber" label={t('report.codReconciliation.posNo')}>
            <Select placeholder={t('report.codReconciliation.allPosOptions')} options={posOptions} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Form.Item name="paymentType" label={t('report.codReconciliation.paymentType')}>
            <Select placeholder={t('common.pleaseSelect')} options={PAYMENT_TYPE_FILTER_OPTIONS} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button icon={<ExportOutlined />} onClick={handleExport} loading={exportLoading}>{t('common.export')}</Button>
      </div>

      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: 1710, y: scrollY }}
            summary={() => (
              // Total 汇总行：Payment Amount 列显示筛选结果全量合计（后端 totalPaymentAmount，非当前页合计）
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={9} align="right">
                    <span style={{ fontWeight: 600 }}>{t('report.codReconciliation.total')}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <span style={{ fontWeight: 600 }}>{formatTHB(totalPaymentAmount)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      </Card>
    </div>
  );
};

export default CodReconciliationReport;
